import { createAjv } from "@jsonforms/core";
import { materialCells, materialRenderers } from "@jsonforms/material-renderers";
import { JsonForms } from "@jsonforms/react";
import { useMemo, useState } from "react";
import { isDeepEqual } from "../../../utils/commons";
import { customerRenderesHelper, getScopesofAllElements } from "./jsonFormsHelper";
import { LayoutTester } from "./testers";
// controllers
import { ConditionalSectionLayoutControl } from "../layouts/conditionalSectionLayout/ConditionalSectionLayout";
import { CustomGroupLayoutControl } from "../layouts/groupLayout/CustomGroupLayout";
import { HorizontalLayoutControl } from "../layouts/horizontalLayout/horizontalLayout";
import { InputRowControl } from "../layouts/inputRow/inputRow";
import { LayerLayoutControl } from "../layouts/layerLayout/LayerLayout";
import { SectionGroupLayoutControl } from "../layouts/sectionGroupLayout/SectionGroupLayout";
import { SimpleGroupControl } from "../layouts/simpleGroup/simpleGroup";
import { SimpleGroupPreviewCardsControl } from "../layouts/simpleGroupPreviewCards/simpleGroupPreviewCards";
import { VerticalLayoutControl } from "../layouts/verticalLayout/verticalLayout";

export default function JsonFormsWrapper({
  jsonschema,
  uischema,
  data = {},
  onChange = () => null,
  readonly = false,
  config = {},
  otherData,
}) {
  const [previousErrors, setPreviousErrors] = useState([]);
  const [prevValues, setPrevValues] = useState(data);
  const scopeAndCompsList = useMemo(
    () => getScopesofAllElements(uischema),
    [uischema]
  );

  const customRenderers = useMemo(
    () => customerRenderesHelper(scopeAndCompsList, otherData || data),
    [scopeAndCompsList, otherData, data]
  );

  const onJsonFormsDataChange = (values, errors) => {
    const changed = !isDeepEqual(values, prevValues);
    const errorsChanged =
      previousErrors.length !== errors.length ||
      JSON.stringify(previousErrors) !== JSON.stringify(errors);

    if (changed || errorsChanged) {
      setPreviousErrors(errors);
      setPrevValues(JSON.parse(JSON.stringify(values)));
      onChange(values, errors);
    }
  };

  const renderers = useMemo(
    () => [
      ...materialRenderers,
      ...customRenderers,
      {
        tester: LayoutTester("VerticalLayout"),
        renderer: VerticalLayoutControl,
      },
      {
        tester: LayoutTester("HorizontalLayout"),
        renderer: HorizontalLayoutControl,
      },
      {
        tester: LayoutTester("SimpleGroup"),
        renderer: SimpleGroupControl,
      },
      {
        tester: LayoutTester("simpleGroupPreviewCards"),
        renderer: SimpleGroupPreviewCardsControl,
      },
      {
        tester: LayoutTester("CustomGroup"),
        renderer: CustomGroupLayoutControl,
      },
      {
        tester: LayoutTester("InputRow"),
        renderer: InputRowControl,
      },
      {
        tester: LayoutTester("ConditionalSectionLayout"),
        renderer: ConditionalSectionLayoutControl,
      },
      {
        tester: LayoutTester("LayerLayout"),
        renderer: LayerLayoutControl,
      },
      {
        tester: LayoutTester("SectionGroupLayout"),
        renderer: SectionGroupLayoutControl,
      },
    ],
    [customRenderers]
  );
  const handleDefaultsAjv = createAjv({ useDefaults: true });
  const safeData = useMemo(() => JSON.parse(JSON.stringify(data)), [data]);

  return (
    <JsonForms
      readonly={readonly}
      schema={jsonschema}
      uischema={uischema}
      data={safeData}
      renderers={renderers}
      cells={materialCells}
      onChange={({ errors, data }) => onJsonFormsDataChange(data, errors)}
      ajv={handleDefaultsAjv}
      config={{ ...config, readonly }}
    />
  );
}
